/**
 * ImprovementMetrics — Loop Health dashboard (S10).
 * Renders conductor-computed OBS metrics for the self-improvement pipeline:
 * pipeline status, flow, quality (with safety alarms), learning, cost, human load.
 */
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  BarChart2,
  Brain,
  CheckCircle,
  Clock,
  DollarSign,
  Gauge,
  ShieldAlert,
  TrendingUp,
  Users,
  XCircle,
  Zap,
} from 'lucide-react';
import { Callout, DataTable, type DataTableColumn, Skeleton, StatStrip } from '../../components/ui/index.js';
import { getImprovementMetricsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { ImprovementTier } from '../../services/api/improvements.api.js';
import type { ImprovementMetrics as ImprovementMetricsData } from '../../generated/api/types.gen.js';
import type { StatStripCell } from '../../components/ui/index.js';
import { AutonomyTierSection } from './ImprovementTierSection.js';

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtPct(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

function fmtUsd(v: number | null | undefined): string {
  if (v == null) return '—';
  return `$${v.toFixed(4)}`;
}

function fmtSec(v: number | null | undefined): string {
  if (v == null) return '—';
  if (v < 60) return `${Math.round(v)}s`;
  if (v < 3600) return `${Math.round(v / 60)}m`;
  return `${(v / 3600).toFixed(1)}h`;
}

function fmtNum(v: number | null | undefined): string {
  if (v == null) return '—';
  return v.toLocaleString();
}

function tierLabel(tier: ImprovementTier): string {
  switch (tier) {
    case 'T0':
      return 'T0 — Ideas only';
    case 'T1':
      return 'T1 — Autonomous + human gate';
    case 'T2':
      return 'T2 — Full autonomy';
  }
}

// ── Sub-sections ───────────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function Section({ title, icon, children }: SectionProps) {
  return (
    <div className="rounded-xl border bg-surface overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b bg-surface-alt">
        <span className="text-faint">{icon}</span>
        <h3 className="text-sm font-semibold text-heading">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

interface MetricRowProps {
  label: string;
  value: React.ReactNode;
  alarm?: boolean;
  warn?: boolean;
  sub?: string;
}

function MetricRow({ label, value, alarm, warn, sub }: MetricRowProps) {
  const valueClass = alarm ? 'text-danger font-semibold' : warn ? 'text-warning font-semibold' : 'text-heading';
  return (
    <div className="flex items-start justify-between py-2 border-b last:border-b-0">
      <div>
        <span className="text-sm text-muted">{label}</span>
        {sub && <p className="text-xs text-faint mt-0.5">{sub}</p>}
      </div>
      <span className={`text-sm tabular-nums ${valueClass}`}>{value}</span>
    </div>
  );
}

interface KVTableProps {
  rows: Array<{ label: string; value: string | React.ReactNode; alarm?: boolean; warn?: boolean; sub?: string }>;
}

function KVTable({ rows }: KVTableProps) {
  return (
    <div className="divide-y">
      {rows.map((r, i) => (
        <MetricRow key={`kv-${i}`} label={r.label} value={r.value} alarm={r.alarm} warn={r.warn} sub={r.sub} />
      ))}
    </div>
  );
}

// ── Pipeline status banner ─────────────────────────────────────────────────────

function PipelineStatusBanner({ pipeline }: { pipeline: ImprovementMetricsData['pipeline'] }) {
  const tone = pipeline.active ? 'teal' : 'danger';
  const Icon = pipeline.active ? CheckCircle : XCircle;

  return (
    <Callout
      tone={tone}
      icon={Icon}
      title={pipeline.active ? 'Pipeline active' : `Pipeline paused — ${pipeline.pauseReason ?? 'no reason given'}`}
    >
      <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1">
        <span>
          Tier: <strong>{tierLabel(pipeline.tier)}</strong>
        </span>
        {pipeline.lastConductorRunAt && (
          <span>
            Last conductor sweep: <strong>{new Date(pipeline.lastConductorRunAt).toLocaleString()}</strong>
          </span>
        )}
        {pipeline.stragglersCount > 0 && (
          <span className="text-warning">
            {pipeline.stragglersCount} straggler{pipeline.stragglersCount !== 1 ? 's' : ''} awaiting re-emit
          </span>
        )}
      </div>
    </Callout>
  );
}

// ── Safety alarms ─────────────────────────────────────────────────────────────

function SafetyAlarms({
  quality,
  humanLoad,
}: {
  quality: ImprovementMetricsData['quality'];
  humanLoad: ImprovementMetricsData['humanLoad'];
}) {
  const violations = quality.integrityViolations30d;
  const revertRate = quality.revertRate ?? 0;
  const rubberStamp = humanLoad.rubberStampDetected;

  const alarms: Array<{
    key: string;
    tone: 'danger' | 'warning';
    icon: typeof AlertTriangle;
    title: string;
    body: string;
  }> = [];

  if (violations > 0) {
    alarms.push({
      key: 'integrity',
      tone: 'danger',
      icon: ShieldAlert,
      title: `${violations} integrity violation${violations !== 1 ? 's' : ''} in the last 30 days`,
      body: 'Evidence bundles failed hash verification. Target: 0. Audit the stage log for tampered proposals.',
    });
  }

  if (revertRate >= 0.03) {
    alarms.push({
      key: 'revert',
      tone: 'danger',
      icon: XCircle,
      title: `Revert rate ${fmtPct(revertRate)} ≥ 3% — tier contraction threshold reached`,
      body: 'Too many applied proposals are being reverted. The system should automatically contract to a lower tier.',
    });
  } else if (revertRate >= 0.015) {
    alarms.push({
      key: 'revert-warn',
      tone: 'warning',
      icon: AlertTriangle,
      title: `Revert rate ${fmtPct(revertRate)} — approaching 3% threshold`,
      body: 'Monitor closely. A revert rate ≥ 3% triggers automatic tier contraction.',
    });
  }

  if (rubberStamp) {
    alarms.push({
      key: 'rubber-stamp',
      tone: 'warning',
      icon: Zap,
      title: 'Rubber-stamp detector fired',
      body: 'A reviewer is approving proposals unusually fast without apparent evidence review. Check median review minutes.',
    });
  }

  if (alarms.length === 0) return null;

  return (
    <div className="space-y-2">
      {alarms.map((a) => (
        <Callout key={a.key} tone={a.tone} icon={a.icon} title={a.title}>
          {a.body}
        </Callout>
      ))}
    </div>
  );
}

// ── Flow section ──────────────────────────────────────────────────────────────

function FlowSection({ flow }: { flow: ImprovementMetricsData['flow'] }) {
  const stageNames = Array.from(
    new Set([...Object.keys(flow.stageLatencyP50Sec), ...Object.keys(flow.wipPerStage)]),
  ).sort();

  const totalWip = Object.values(flow.wipPerStage).reduce((a, b) => a + b, 0);
  const totalIntake = Object.values(flow.intakePerDayByLane).reduce((a, b) => a + b, 0);

  const expiryHighlighted: StatStripCell['severity'] = (flow.expiryRate ?? 0) > 0.1 ? 'warning' : undefined;
  const stripCells: StatStripCell[] = [
    { label: 'Intake / day', value: fmtNum(totalIntake) },
    { label: 'WIP total', value: fmtNum(totalWip) },
    { label: 'Expiry rate', value: fmtPct(flow.expiryRate), severity: expiryHighlighted },
    {
      label: 'Time to decision (p50)',
      value: flow.timeToDecisionHrsP50 != null ? `${flow.timeToDecisionHrsP50.toFixed(1)}h` : '—',
    },
  ];

  const stageColumns: DataTableColumn<string>[] = [
    {
      key: 'stage',
      header: 'Stage',
      render: (stage) => <span className="capitalize">{stage.replace(/_/g, ' ')}</span>,
    },
    { key: 'wip', header: 'WIP', align: 'right', render: (stage) => fmtNum(flow.wipPerStage[stage]) },
    { key: 'p50', header: 'Latency p50', align: 'right', render: (stage) => fmtSec(flow.stageLatencyP50Sec[stage]) },
    { key: 'p95', header: 'Latency p95', align: 'right', render: (stage) => fmtSec(flow.stageLatencyP95Sec[stage]) },
  ];

  return (
    <Section title="Flow" icon={<Activity size={16} />}>
      <StatStrip cells={stripCells} className="mb-4" />

      {/* Stage latency + WIP grid */}
      {stageNames.length > 0 && (
        <DataTable
          aria-label="Stage latency and work in progress"
          columns={stageColumns}
          rows={stageNames}
          rowKey={(stage) => stage}
          framed
        />
      )}

      {/* Intake by lane */}
      {Object.keys(flow.intakePerDayByLane).length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-medium text-muted mb-2">Intake / day by lane</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(flow.intakePerDayByLane).map(([lane, count]) => (
              <span key={lane} className="text-xs bg-surface-alt border rounded-sm px-2 py-1 text-body">
                {lane.replace(/_/g, ' ')}: <span className="font-semibold text-heading">{count.toFixed(1)}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

// ── Quality section ────────────────────────────────────────────────────────────

function QualitySection({ quality }: { quality: ImprovementMetricsData['quality'] }) {
  const revertRate = quality.revertRate ?? 0;
  const revertAlarm = revertRate >= 0.03;
  const revertWarn = !revertAlarm && revertRate >= 0.015;

  const rows = [
    {
      label: 'Acceptance rate (overall)',
      value: fmtPct(quality.acceptanceRateOverall),
      sub: 'Fraction of human-decided proposals approved',
    },
    {
      label: 'Revert rate',
      value: fmtPct(quality.revertRate),
      alarm: revertAlarm,
      warn: revertWarn,
      sub: 'Target: <3% — triggers tier contraction at ≥3%',
    },
    {
      label: 'Audit first-pass rate',
      value: fmtPct(quality.auditFirstPassRate),
      sub: 'Proposals passing audit without rework',
    },
    {
      label: 'Strict vs loose delta',
      value: quality.strictVsLooseDelta != null ? quality.strictVsLooseDelta.toFixed(3) : '—',
      sub: 'Delta between strict and loose audit modes (target ≈ 0)',
    },
    {
      label: 'Integrity violations (30d)',
      value: quality.integrityViolations30d,
      alarm: quality.integrityViolations30d > 0,
      sub: 'Target: 0',
    },
  ];

  const laneNames = Object.keys(quality.acceptanceRateByLane);

  return (
    <Section title="Quality" icon={<BarChart2 size={16} />}>
      <KVTable rows={rows} />

      {laneNames.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-medium text-muted mb-2">Acceptance rate by lane</p>
          <div className="flex flex-wrap gap-2">
            {laneNames.map((lane) => (
              <span key={lane} className="text-xs bg-surface-alt border rounded-sm px-2 py-1 text-body">
                {lane.replace(/_/g, ' ')}:{' '}
                <span className="font-semibold text-heading">{fmtPct(quality.acceptanceRateByLane[lane])}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

// ── Learning section ───────────────────────────────────────────────────────────

function LearningSection({ learning }: { learning: ImprovementMetricsData['learning'] }) {
  const proposerEntries = Object.entries(learning.brierScoreByProposer);
  const banditEntries = Object.entries(learning.banditArmPosterior).sort(([, a], [, b]) => (b ?? 0) - (a ?? 0));

  return (
    <Section title="Learning" icon={<Brain size={16} />}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Calibration */}
        <div>
          <p className="text-xs font-medium text-muted mb-2">Brier calibration by proposer</p>
          {proposerEntries.length === 0 ? (
            <p className="text-xs text-faint">No data yet</p>
          ) : (
            <div className="divide-y">
              {proposerEntries.map(([daemon, score]) => (
                <div key={daemon} className="flex justify-between py-1.5 text-xs">
                  <span className="text-muted truncate max-w-[60%]">{daemon}</span>
                  <span className="text-heading tabular-nums">{score != null ? score.toFixed(4) : '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bandit posteriors */}
        <div>
          <p className="text-xs font-medium text-muted mb-2">Bandit arm posterior (higher = favoured)</p>
          {banditEntries.length === 0 ? (
            <p className="text-xs text-faint">No data yet</p>
          ) : (
            <div className="divide-y">
              {banditEntries.map(([arm, posterior]) => (
                <div key={arm} className="flex justify-between py-1.5 text-xs">
                  <span className="text-muted truncate max-w-[60%]">{arm.replace(/_/g, ' ')}</span>
                  <span className="text-heading tabular-nums">{posterior != null ? posterior.toFixed(4) : '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lineage fertility */}
      {learning.lineageFertilityTopN.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-medium text-muted mb-2">Top lineage roots by fertile descendants</p>
          <div className="divide-y">
            {learning.lineageFertilityTopN.map((item) => (
              <div key={item.proposalId} className="flex justify-between py-1.5 text-xs">
                <span className="text-muted truncate max-w-[75%]">
                  #{item.proposalId} — {item.title}
                </span>
                <span className="text-heading tabular-nums">
                  <TrendingUp size={10} className="inline mr-1 text-success" />
                  {item.fertileDescendants}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

// ── Cost section ───────────────────────────────────────────────────────────────

function CostSection({ cost }: { cost: ImprovementMetricsData['cost'] }) {
  const rows = [
    {
      label: 'Cost / proposal (p50)',
      value: fmtUsd(cost.costPerProposalP50Usd),
      sub: 'Paid model USD per proposal processed',
    },
    { label: 'Cost / proposal (p95)', value: fmtUsd(cost.costPerProposalP95Usd) },
    {
      label: 'Cost / accepted proposal (p50)',
      value: fmtUsd(cost.costPerAcceptedProposalP50Usd),
      sub: 'Includes apply-chain costs',
    },
    {
      label: 'Local token volume',
      value: fmtNum(cost.localTokenVolumeTotal),
      sub: 'Informational — local compute is free by policy',
    },
  ];

  return (
    <Section title="Cost" icon={<DollarSign size={16} />}>
      <KVTable rows={rows} />
    </Section>
  );
}

// ── Human load section ─────────────────────────────────────────────────────────

function HumanLoadSection({ humanLoad }: { humanLoad: ImprovementMetricsData['humanLoad'] }) {
  const rows = [
    { label: 'Decisions (last 7 days)', value: humanLoad.decisionsLast7Days.toString() },
    {
      label: 'Median review time',
      value: humanLoad.medianReviewMinutes != null ? `${humanLoad.medianReviewMinutes.toFixed(1)} min` : '—',
      sub: 'From ready_for_human to decision',
    },
    {
      label: 'Rubber-stamp detector',
      value: humanLoad.rubberStampDetected ? (
        <span className="text-warning font-semibold">FIRED</span>
      ) : (
        <span className="text-success">Clear</span>
      ),
      warn: humanLoad.rubberStampDetected,
      sub: 'Fires when reviewer approves too fast without evidence review',
    },
    {
      label: 'Fast-review fraction',
      value: fmtPct(humanLoad.fastReviewFraction),
      warn: (humanLoad.fastReviewFraction ?? 0) > 0.4,
      sub: 'Fraction of reviews completed within 5 min (high = potential rubber-stamping)',
    },
  ];

  return (
    <Section title="Human Load" icon={<Users size={16} />}>
      <KVTable rows={rows} />
    </Section>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function ImprovementMetrics() {
  const { data, isLoading, error } = useQuery({ ...getImprovementMetricsOptions(), staleTime: 60_000, retry: false });

  if (isLoading) {
    return (
      <div className="space-y-4" aria-busy="true" aria-label="Loading metrics">
        <Skeleton className="h-12 rounded-xl" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    const is404 = error instanceof Error && (error.message.includes('404') || error.message.includes('HTTP 404'));

    return (
      <Callout tone="accent" icon={Gauge} title="Metrics not yet available">
        {is404
          ? 'The conductor daemon has not completed its first sweep yet. Metrics will appear after the pipeline runs for the first time.'
          : `Failed to load metrics: ${(error as Error)?.message ?? 'unknown error'}`}
      </Callout>
    );
  }

  const { pipeline, flow, quality, learning, cost, humanLoad, computedAt } = data;

  return (
    <div className="space-y-4">
      {/* Pipeline status banner */}
      <PipelineStatusBanner pipeline={pipeline} />

      {/* Safety alarms — show before metric sections */}
      <SafetyAlarms quality={quality} humanLoad={humanLoad} />

      {/* Quick-glance safety strip */}
      <StatStrip
        cells={[
          {
            label: 'Integrity violations (30d)',
            value: quality.integrityViolations30d,
            severity: quality.integrityViolations30d > 0 ? 'danger' : 'success',
            icon: ShieldAlert,
            sub: 'Target: 0',
          },
          {
            label: 'Revert rate',
            value: fmtPct(quality.revertRate),
            severity:
              (quality.revertRate ?? 0) >= 0.03 ? 'danger' : (quality.revertRate ?? 0) >= 0.015 ? 'warning' : 'success',
            icon: XCircle,
            sub: 'Gate at 3%',
          },
          {
            label: 'Rubber-stamp',
            value: humanLoad.rubberStampDetected ? 'FIRED' : 'Clear',
            severity: humanLoad.rubberStampDetected ? 'warning' : 'success',
            icon: Zap,
          },
          {
            label: 'Active tier',
            value: pipeline.tier,
            severity: pipeline.active ? 'success' : 'danger',
            icon: Clock,
          },
        ]}
      />

      {/* Metric sections — 2-col on large screens */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <FlowSection flow={flow} />
        <QualitySection quality={quality} />
        <LearningSection learning={learning} />
        <CostSection cost={cost} />
      </div>

      <HumanLoadSection humanLoad={humanLoad} />

      {/* Autonomy Tier — per-lane T0/T1/T2 state and superadmin expand controls */}
      <AutonomyTierSection />

      {computedAt && (
        <p className="text-xs text-faint text-right">Aggregates computed at {new Date(computedAt).toLocaleString()}</p>
      )}
    </div>
  );
}
